'use client';
import { MultiTierGift } from '@eliadmin/core/src/components/product/detail/multi-tier-gift';
import { OFFER_CARD, OfferGroup } from '@eliadmin/core/src/components/dlz/product-detail/extras/offer-parts';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { GiftCouponValue } from '@eliadmin/core/src/components/product/gift-coupon-value';
import { GiftPackageLabel } from '@eliadmin/core/src/components/product/gift-package-label';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { getGiftItemDisplay } from '@eliadmin/core/src/utils/cart-gift-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export const ProductGifts = () => {
  const product = useVariantsStore(s => s.product)!;
  const { t } = useTranslations();
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  if (!product.gifts?.length) return null;
  return (
    <OfferGroup title={t('text664')} className="ea-product-detail-gifts">
      <ul className="flex flex-col gap-2">
        {product.gifts.map(rule => (rule.tiers.length > 1 ? <li key={rule.id}><MultiTierGift gift={rule} heading={t('text664')} /></li> : (<li key={rule.id} data-gift-id={rule.id} data-gift-presentation={rule.tiers.length === 1 ? 'single' : 'multiple'} className="flex flex-col gap-2">
            {rule.tiers.length > 1 && <h4 className="dlz-body-s-medium min-w-0 flex-1 text-[#0F1012]">{rule.name}</h4>}
            <ul className="flex flex-col gap-2">
              {rule.tiers.map(tier => (
                <li key={tier.id} data-gift-tier-id={tier.id} className={cn('flex flex-col gap-3', OFFER_CARD, 'ea-product-detail-gifts__item')}>
                  <div className="flex items-center gap-3">
                    <Icon name="gift" size={20} className="shrink-0 text-[#D7191F]" />
                    <h5 className="dlz-body-s-medium min-w-0 flex-1 text-[#0F1012]">{rule.tiers.length === 1 ? rule.name : tier.name}</h5>
                    {tier.order_price !== null && <span className={cn('dlz-caption whitespace-nowrap rounded-[6px] px-2 py-1 font-semibold', Number(tier.order_price) === 0 ? 'bg-[#ECF7EF] text-[#16753A]' : 'bg-[#F5F6F7] text-[#0F1012]')}>
                      {Number(tier.order_price) === 0 ? t('text3230') : `${t('text3229')} ${rule.tiers.length === 1 ? formatPrice(tier.order_price, currency, currencySettings).replace(/([.,])0+(\s|$)/, '$2') : formatPrice(tier.order_price, currency, currencySettings)}`}
                    </span>}
                  </div>
                  {tier.reward_coupon && <div className="flex items-center gap-3">
                    <GiftCouponValue coupon={tier.reward_coupon} className="size-14 rounded-[12px] border-[#FBDCDC] bg-[#FDF0F0] text-base text-[#D7191F]" />
                    <span className="dlz-body-s-medium text-[#0F1012]">{tier.reward_coupon.name}</span>
                  </div>}
                  {rule.tiers.length > 1 && tier.items.length > 0 && <GiftPackageLabel />}
                  {tier.items.map(item => {
                    const display = getGiftItemDisplay(item);
                    const entity = item.variant ?? item.product;
                    const price = entity ? getProductPriceDisplay(entity) : null;
                    return <div key={item.id} className="flex items-center gap-3">
                      <Image src={display.image?.url ?? 'images/placeholder.png'} alt={resolveImageAlt(display.image, display.name, tier.name)} width={56} height={56} className="size-14 shrink-0 rounded-[12px] bg-[#F5F6F7] object-contain" unoptimized />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="dlz-body-s-medium text-[#0F1012]">{(rule.tiers.length > 1 || Number(display.quantity) !== 1) && <>{display.quantity} × </>}{display.name}</span>
                        {price && !price.priceOnRequest && <span className="dlz-caption text-[#6B7079]">{t('text3228')} {formatPrice(price.current, currency, currencySettings)}</span>}
                      </div>
                    </div>;
                  })}
                </li>
              ))}
            </ul>
          </li>)))}
      </ul>
    </OfferGroup>
  );
};
